import React, { FC, useState } from 'react';
import {
    TableCell,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TableContainer,
    Table,
    TableHead,
    TableBody,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'utils/format';
import MonetaryValueInput from 'components/monetary-value/monetary-value.component';

type PriceSupplementAccordionProps = {
    clientCurrency: string;
    materialGroup: MaterialGroup | undefined;
    showAllPriceSupplement: boolean;
    isEdit: boolean;
    canEditActionList: boolean;
    onMaterialGroupChange: (materialGroup: MaterialGroup) => void;
};

type MaterialGroup = {
    materialGroupId: string;
    materialGroupName: string;
    materials: {
        material: string;
        materialName: string;
        priceDiff: number | string;
    }[];
};
const PriceSupplementAccordion: FC<PriceSupplementAccordionProps> = ({
    clientCurrency,
    materialGroup,
    showAllPriceSupplement,
    isEdit,
    canEditActionList,
    onMaterialGroupChange,
}: PriceSupplementAccordionProps) => {
    const { t: translate } = useTranslation(['client-directory', 'orders']);
    const [showItemPriceSupplement, setShowItemPriceSupplementTable] = useState(false);

    const toggleShowItemPriceSupplement = () => {
        setShowItemPriceSupplementTable((prev) => !prev);
    };

    const handlePriceChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        materialId: string,
    ) => {
        const regex = /^-?\d*[,.]?\d{0,2}$/;
        const value = event.target.value || 0;
        if (value && !regex.test(value)) {
            return event.preventDefault();
        }
        const newMaterials = materialGroup?.materials?.map((material) => {
            if (material.material === materialId) {
                return { ...material, priceDiff: value };
            }
            return material;
        });
        if (newMaterials) {
            onMaterialGroupChange({ ...materialGroup, materials: newMaterials } as MaterialGroup);
        }
    };

    return (
        <Accordion
            expanded={showAllPriceSupplement || showItemPriceSupplement || (canEditActionList && isEdit)}
            elevation={0}
            sx={{
                '&.MuiAccordion-root:before': {
                    backgroundColor: 'white',
                },
                '&:hover': {
                    backgroundColor: '#f5f5f5',
                },
                padding: 0.5,
                margin: 0,
                width: 250,
            }}
        >
            <AccordionSummary
                onClick={toggleShowItemPriceSupplement}
                expandIcon={<ExpandMoreIcon />}
                id="material-price-supplement"
                sx={{ padding: 0, margin: 0 }}
            >
                {translate('client-directory:PRICE_SUPPLEMENT')}
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 1, margin: 0 }}>
                <TableContainer>
                    <Table size="small" padding="none">
                        <TableHead>
                            <TableRow>
                                <TableCell size="small">{translate('orders:MATERIAL')}</TableCell>
                                <TableCell size="small" align="right">
                                    {translate('client-directory:SUPPLEMENT')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {materialGroup?.materials?.map((material, index) => (
                                <TableRow key={index}>
                                    <TableCell>{material.materialName}</TableCell>
                                    {canEditActionList && isEdit ? (
                                        <TableCell align="right" width={50}>
                                            <MonetaryValueInput
                                                value={material.priceDiff || ''}
                                                variant="standard"
                                                hiddenLabel
                                                size="small"
                                                onChange={(e) => handlePriceChange(e, material.material)}
                                            />
                                        </TableCell>
                                    ) : (
                                        <TableCell align="right">
                                            {formatCurrency(material.priceDiff as number, clientCurrency)}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </AccordionDetails>
        </Accordion>
    );
};

export default PriceSupplementAccordion;
